/**
 * The width the tap slot adds to the first visible data cell of a collapsed
 * row: a 10px triangle and an 18px gap before the text (body), and the header
 * padded the same 28px beyond its usual inset. Both live in
 * datatable_tailwind.css.
 */
export const TAP_SLOT_WIDTH = 28;

/**
 * A report table (`.datatable-report`) insets its first column by 24px, not
 * the usual 16px, and in Responsive mode gives the same inset to whichever
 * column ends up first visible. When that is not the table's first column the
 * slot column is therefore 8px wider than the copy measured, on top of the
 * slot itself.
 */
export const REPORT_FIRST_CELL_EXTRA_INSET = 8;

/** The slot's cost on each column, for a table that is or is not a report. */
export const tapSlotWidthFor =
  (report: boolean) =>
  (index: number): number =>
    TAP_SLOT_WIDTH +
    (report && index !== 0 ? REPORT_FIRST_CELL_EXTRA_INSET : 0);

export interface ResponsiveColumn {
  /** False for a column Responsive never hides (the wrapper's `all`). */
  auto: boolean;
  /** The column's smallest width, measured on Responsive's sizing copy. */
  minWidth: number;
}

type Visibility = Array<boolean | string>;

/**
 * Responsive decides which columns fit from widths measured on a copy of the
 * table, but the tap slot sits on whichever data column ends up first
 * visible, which that copy cannot know. Measured against the leftmost data
 * column (or none, when the table was not collapsed), the fit is wrong when
 * the slot lands elsewhere: too wide by the slot, or, fed back through the
 * next measurement, flipping one column on and off without end.
 *
 * `decide` is Responsive's own decision over `columns`; `measuredOn` is the
 * column whose measured width already includes the slot, or null; `available`
 * is the width Responsive fits into. The slot is moved to the column it would
 * land on and the decision re-run until the two agree. If they never do, the
 * widest candidate that fits with the slot on its own first visible column
 * wins, else the most collapsed one. Measured widths are restored before
 * returning. `slot` is the slot's width, or its cost per column index when
 * that differs by column (see `tapSlotWidthFor`).
 */
export function decideWithTapSlot(
  columns: ResponsiveColumn[],
  isShown: (index: number) => boolean,
  measuredOn: number | null,
  available: number,
  decide: () => Visibility,
  slot: number | ((index: number) => number) = TAP_SLOT_WIDTH
): Visibility {
  const slotOf = typeof slot === "function" ? slot : () => slot;
  const measured = columns.map((column) => column.minWidth);
  const dataColumn = (index: number) => columns[index].auto && isShown(index);
  const slotColumn = (visibility: Visibility) => {
    const collapsed = visibility.some(
      (shown, index) => dataColumn(index) && shown === false
    );
    if (!collapsed) return null;
    const first = visibility.findIndex(
      (shown, index) => dataColumn(index) && shown === true
    );
    return first === -1 ? null : first;
  };
  const visibleCount = (visibility: Visibility) =>
    visibility.filter((shown) => shown === true).length;
  // Each column's width without the slot, and a candidate's total with the
  // slot where it would really sit.
  const bare = measured.map((width, index) =>
    index === measuredOn ? width - slotOf(index) : width
  );
  const required = (visibility: Visibility) => {
    const slotOn = slotColumn(visibility);
    return visibility.reduce<number>(
      (sum, shown, index) =>
        shown === true
          ? sum + bare[index] + (index === slotOn ? slotOf(index) : 0)
          : sum,
      0
    );
  };

  let slotOn = measuredOn;
  let visibility = decide();
  const candidates = [visibility];
  try {
    for (let pass = 0; pass < 3; pass++) {
      const wanted = slotColumn(visibility);
      if (wanted === slotOn) return visibility;
      if (slotOn !== null) columns[slotOn].minWidth -= slotOf(slotOn);
      if (wanted !== null) columns[wanted].minWidth += slotOf(wanted);
      slotOn = wanted;
      visibility = decide();
      candidates.push(visibility);
    }
    const fitting = candidates.filter(
      (candidate) => required(candidate) <= available
    );
    if (fitting.length > 0) {
      return fitting.reduce((most, candidate) =>
        visibleCount(candidate) > visibleCount(most) ? candidate : most
      );
    }
    return candidates.reduce((fewest, candidate) =>
      visibleCount(candidate) < visibleCount(fewest) ? candidate : fewest
    );
  } finally {
    columns.forEach((column, index) => {
      column.minWidth = measured[index];
    });
  }
}
